import { createComparator } from '../createComparator';
import { getAbsoluteUrl } from '../getAbsoluteUrl';
import { hrefFromURL } from '../hrefFromURL';
import { DEFAULT_ZOOM_LEVEL_SHORTCUT_DELTA, GEOMETRY_LAYOUT, INTERACTION, LAYER_IDS, LAYER_TYPE, } from './constants';
import { DEFAULT_SEARCH_TABS } from './deriveMapSearchStateFromConfig';
const DEFAULT_TILE_SIZE = 256;
const DEFAULT_LAYER_Z_INDEX = 0;
const SYSTEM_LAYER_Z_INDEX = 10000;
const COLOR = {
    DEFAULT: [3, 155, 229],
    HIGHLIGHT: [204, 62, 216],
    ACTIVE: [224, 70, 78],
    OUTLINE: [255, 255, 255],
};
const CIRCLE_PAINT = {
    'circle-color': `rgba(${COLOR.DEFAULT[0]}, ${COLOR.DEFAULT[1]}, ${COLOR.DEFAULT[2]}, 1)`,
    'circle-stroke-width': 2,
    'circle-stroke-color': `rgba(${COLOR.OUTLINE[0]}, ${COLOR.OUTLINE[1]}, ${COLOR.OUTLINE[2]}, 1)`,
    'circle-radius': 6,
};
const CIRCLE_HIGHLIGHT_PAINT = {
    'circle-color': `rgba(${COLOR.HIGHLIGHT[0]}, ${COLOR.HIGHLIGHT[1]}, ${COLOR.HIGHLIGHT[2]}, 1)`,
    'circle-stroke-width': 2,
    'circle-stroke-color': `rgba(${COLOR.OUTLINE[0]}, ${COLOR.OUTLINE[1]}, ${COLOR.OUTLINE[2]}, 1)`,
    'circle-radius': 6,
};
const FILL_PAINT = {
    'fill-color': `rgba(${COLOR.DEFAULT[0]}, ${COLOR.DEFAULT[1]}, ${COLOR.DEFAULT[2]}, 0.3)`,
};
const FILL_HIGHLIGHT_PAINT = {
    'fill-outline-color': `rgba(${COLOR.HIGHLIGHT[0]}, ${COLOR.HIGHLIGHT[1]}, ${COLOR.HIGHLIGHT[2]}, 1)`,
    'fill-color': `rgba(${COLOR.HIGHLIGHT[0]}, ${COLOR.HIGHLIGHT[1]}, ${COLOR.HIGHLIGHT[2]}, 0.2)`,
};
const LINE_PAINT = {
    'line-width': 2,
    'line-color': `rgba(${COLOR.DEFAULT[0]}, ${COLOR.DEFAULT[1]}, ${COLOR.DEFAULT[2]}, 1)`,
};
const THICK_LINE_PAINT = {
    'line-width': 4,
    'line-color': `rgba(${COLOR.DEFAULT[0]}, ${COLOR.DEFAULT[1]}, ${COLOR.DEFAULT[2]}, 1)`,
};
const LINE_HIGHLIGHT_PAINT = {
    'line-width': 2,
    'line-color': `rgba(${COLOR.HIGHLIGHT[0]}, ${COLOR.HIGHLIGHT[1]}, ${COLOR.HIGHLIGHT[2]}, 1)`,
};
const interactionInitialState = {
    type: INTERACTION.NONE,
    selectedFeatureIds: [],
    layerId: '',
    originLayerId: null,
    drawGeometryType: 'LineString',
    geometryLayout: GEOMETRY_LAYOUT.XY,
    highlightedFeatures: [],
    coordinate: null,
    highlightedFeaturesCount: Infinity,
    snap: {
        layerIds: [],
        features: [],
        layerFeatureIds: [],
        pixelTolerance: 10,
        edges: true,
        vertices: true,
    },
    operationMethod: undefined,
};
const DEFAULT_ZOOM_LEVEL_SHORTCUTS = [
    { id: 'kraj', zoom: 11 },
    { id: 'obec', zoom: 14 },
    { id: 'katastralni_uzemi', zoom: 17 },
    { id: 'parcela', zoom: 20 },
    { id: 'dtm', zoom: 23 },
];
const deriveStateFromZoomLevelShortcutsConfig = (zoomLevelShortcuts, minZoom, maxZoom) => {
    if (!zoomLevelShortcuts)
        return null;
    let sortedConfig = [...zoomLevelShortcuts];
    if (!zoomLevelShortcuts.length) {
        sortedConfig = DEFAULT_ZOOM_LEVEL_SHORTCUTS.filter((zoomLevelShortcut) => zoomLevelShortcut.zoom >= minZoom && zoomLevelShortcut.zoom <= maxZoom);
    }
    sortedConfig.sort(createComparator((_, config) => config.zoom));
    return sortedConfig.map((config) => ({
        ...config,
        delta: config.delta ?? DEFAULT_ZOOM_LEVEL_SHORTCUT_DELTA,
    }));
};
const deriveStateFromMapcoreConfig = (config) => {
    if (!config || !config.view || !config.layers?.length) {
        return;
    }
    let projection = 'EPSG:3857';
    const projectionDefinition = [];
    switch (config.view.projection) {
        case 'EPSG:5514':
            projectionDefinition.push([
                'EPSG:5514',
                '+proj=krovak +lat_0=49.5 +lon_0=24.83333333333333 +alpha=0 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=570.8,85.7,462.8,4.998,1.587,5.261,3.56 +units=m +no_defs',
            ]);
            projection = config.view.projection;
            break;
        case 'EPSG:4326':
            projection = config.view.projection;
    }
    const minZoom = config.view.minZoom ?? 0;
    const maxZoom = config.view.maxZoom ?? 21;
    const zoom = Math.min(maxZoom, Math.max(minZoom, config.view.zoom ?? 0));
    return {
        interaction: { ...interactionInitialState },
        view: {
            ...config.view,
            setExtent: undefined,
            zoom,
            maxZoom,
            minZoom,
            rotation: config.view.rotation ?? 0,
            projection,
            projectionDefinition,
            resolution: undefined,
        },
        zoomLevelShortcuts: deriveStateFromZoomLevelShortcutsConfig(config.zoomLevelShortcuts, minZoom, maxZoom),
        layers: config.layers.map((layer) => {
            const url = hrefFromURL(getAbsoluteUrl(layer.sourceOptions.url || (typeof window !== 'undefined' ? window.location.href : '')));
            const layerOptions = {
                visible: !!layer.layerOptions?.visible,
                zIndex: DEFAULT_LAYER_Z_INDEX,
                opacity: layer.layerOptions?.opacity ?? 1,
                minResolution: layer.layerOptions?.minResolution || undefined,
                maxResolution: layer.layerOptions?.maxResolution || undefined,
            };
            switch (layer.type) {
                case LAYER_TYPE.FEATURE_ARCGIS:
                case LAYER_TYPE.VECTOR: {
                    return {
                        ...layer,
                        sourceOptions: {
                            ...layer.sourceOptions,
                            url,
                        },
                        layerOptions,
                        features: [],
                        highlightedFeatureIds: layer.type === LAYER_TYPE.VECTOR ? [] : undefined,
                    };
                }
                case LAYER_TYPE.TILE_VECTOR: {
                    const resolutions = Array.isArray(layer.gridParams?.resolutions)
                        ? layer.gridParams?.resolutions
                        : (layer.gridParams?.resolutions &&
                            config.resolutions?.[layer.gridParams.resolutions]) ||
                            undefined;
                    const gridParams = layer.gridParams &&
                        {
                            ...layer.gridParams,
                            tileSize: layer.gridParams.tileSize || DEFAULT_TILE_SIZE,
                            resolutions,
                        };
                    return {
                        version: 0,
                        ...layer,
                        sourceOptions: {
                            ...layer.sourceOptions,
                            url,
                        },
                        layerOptions,
                        gridParams,
                        hiddenFeatureIds: [],
                        highlightedFeatureIds: [],
                    };
                }
                case LAYER_TYPE.TILE:
                case LAYER_TYPE.TILE_ARCGIS:
                case LAYER_TYPE.TILE_ARCGIS_REST:
                case LAYER_TYPE.WMTS: {
                    const resolutions = Array.isArray(layer.gridParams?.resolutions)
                        ? layer.gridParams?.resolutions
                        : (layer.gridParams?.resolutions &&
                            config.resolutions?.[layer.gridParams.resolutions]) ||
                            undefined;
                    const gridParams = layer.gridParams &&
                        {
                            ...layer.gridParams,
                            tileSize: layer.gridParams.tileSize || DEFAULT_TILE_SIZE,
                            resolutions,
                        };
                    return {
                        ...layer,
                        sourceOptions: {
                            ...layer.sourceOptions,
                            url,
                        },
                        layerOptions,
                        gridParams,
                    };
                }
                default: {
                    return { ...layer, layerOptions };
                }
            }
        }),
    };
};
const deriveLayerFromMeasureConfig = () => ({
    id: LAYER_IDS.MEASURE,
    type: LAYER_TYPE.VECTOR,
    layerOptions: { visible: true, zIndex: SYSTEM_LAYER_Z_INDEX, opacity: 1 },
    sourceOptions: {
        format: 'empty',
    },
    features: [],
    highlightedFeatureIds: [],
    style: 'MEASURE',
});
const deriveLayersFromNotesConfig = () => [
    {
        id: LAYER_IDS.NOTES,
        type: LAYER_TYPE.VECTOR,
        layerOptions: { visible: true, zIndex: SYSTEM_LAYER_Z_INDEX, opacity: 1 },
        sourceOptions: {
            format: 'empty',
        },
        features: [],
        highlightedFeatureIds: [],
        style: 'NOTE',
    },
    {
        id: LAYER_IDS.NOTES_MODIFY,
        type: LAYER_TYPE.VECTOR,
        layerOptions: { visible: true, zIndex: SYSTEM_LAYER_Z_INDEX, opacity: 1 },
        sourceOptions: {
            format: 'empty',
        },
        features: [],
        highlightedFeatureIds: [],
        style: 'NOTE.active',
    },
];
const deriveLayerFromHighlightConfig = () => ({
    id: LAYER_IDS.HIGHLIGHT,
    type: LAYER_TYPE.VECTOR,
    layerOptions: { visible: true, zIndex: SYSTEM_LAYER_Z_INDEX, opacity: 1 },
    sourceOptions: {
        format: 'empty',
    },
    features: [],
    highlightedFeatureIds: [],
    style: 'HIGHLIGHT',
    highlightStyle: 'HIGHLIGHT',
});
const deriveLayersFromUIManagerConfig = () => [
    {
        id: LAYER_IDS.EDIT_TOOLS_HIGHLIGHT,
        type: LAYER_TYPE.VECTOR,
        sourceOptions: {
            format: 'empty',
        },
        style: 'DEFAULT',
        highlightStyle: 'HIGHLIGHT',
        highlightedFeatureIds: [],
        layerOptions: { visible: true, zIndex: SYSTEM_LAYER_Z_INDEX, opacity: 1 },
        features: [],
    },
    {
        id: LAYER_IDS.EDIT_TOOLS_MODIFY,
        type: LAYER_TYPE.VECTOR,
        sourceOptions: {
            format: 'empty',
        },
        style: 'ACTIVE',
        highlightStyle: 'ACTIVE',
        highlightedFeatureIds: [],
        layerOptions: { visible: true, zIndex: SYSTEM_LAYER_Z_INDEX, opacity: 1 },
        features: [],
    },
    {
        id: LAYER_IDS.EDIT_TOOLS_SNAP,
        type: LAYER_TYPE.VECTOR,
        sourceOptions: {
            format: 'empty',
        },
        style: 'MEASURE',
        highlightStyle: 'MEASURE',
        highlightedFeatureIds: [],
        layerOptions: { visible: true, zIndex: SYSTEM_LAYER_Z_INDEX, opacity: 1 },
        features: [],
    },
];
const deriveLayerFromMapSearchConfig = (config) => {
    const tabs = config?.tabs ?? [];
    if (config?.addDefaultTabs) {
        tabs.push(DEFAULT_SEARCH_TABS.adresy, DEFAULT_SEARCH_TABS.parcely, DEFAULT_SEARCH_TABS.coords);
    }
    return tabs.map((tab) => {
        const source = `${LAYER_IDS.SEARCH}-${tab.id}`;
        const layers = [
            {
                id: source,
                source,
                'source-layer': source,
                type: 'circle',
                paint: CIRCLE_PAINT,
            },
        ];
        if (tab.isFilled) {
            layers.push({
                id: source,
                source,
                'source-layer': source,
                type: 'fill',
                paint: FILL_PAINT,
            });
        }
        switch (tab.outline) {
            case undefined:
            case 'Thin':
                layers.push({
                    id: source,
                    source,
                    'source-layer': source,
                    type: 'line',
                    paint: LINE_PAINT,
                });
                break;
            case 'Thick':
                layers.push({
                    id: source,
                    source,
                    'source-layer': source,
                    type: 'line',
                    paint: THICK_LINE_PAINT,
                });
                break;
        }
        return {
            id: source,
            type: LAYER_TYPE.VECTOR,
            layerOptions: { visible: true, zIndex: SYSTEM_LAYER_Z_INDEX, opacity: 1 },
            sourceOptions: { format: 'empty' },
            features: [],
            highlightedFeatureIds: [],
            mapboxStyle: {
                version: 8,
                sources: {
                    [source]: { type: 'vector' },
                },
                layers,
            },
        };
    });
};
const MAPBOX_LAYER_TYPE = {
    [LAYER_TYPE.VECTOR]: 'vector',
};
const deriveLayersFromInfoConfig = () => [
    {
        id: LAYER_IDS.INFO,
        type: LAYER_TYPE.VECTOR,
        layerOptions: { visible: true, zIndex: SYSTEM_LAYER_Z_INDEX, opacity: 1 },
        sourceOptions: {
            format: 'empty',
        },
        features: [],
        highlightedFeatureIds: [],
    },
    {
        id: LAYER_IDS.INFO_RESULTS,
        type: LAYER_TYPE.VECTOR,
        layerOptions: { visible: true, zIndex: SYSTEM_LAYER_Z_INDEX, opacity: 1 },
        sourceOptions: {
            format: 'empty',
        },
        features: [],
        highlightedFeatureIds: [],
        mapboxStyle: {
            version: 8,
            sources: {
                [LAYER_IDS.INFO_RESULTS]: {
                    type: MAPBOX_LAYER_TYPE[LAYER_TYPE.VECTOR],
                },
            },
            layers: [
                {
                    id: LAYER_IDS.INFO_RESULTS,
                    source: LAYER_IDS.INFO_RESULTS,
                    'source-layer': LAYER_IDS.INFO_RESULTS,
                    type: 'circle',
                    paint: CIRCLE_HIGHLIGHT_PAINT,
                },
                {
                    id: LAYER_IDS.INFO_RESULTS,
                    source: LAYER_IDS.INFO_RESULTS,
                    'source-layer': LAYER_IDS.INFO_RESULTS,
                    type: 'fill',
                    paint: FILL_HIGHLIGHT_PAINT,
                },
                {
                    id: LAYER_IDS.INFO_RESULTS,
                    source: LAYER_IDS.INFO_RESULTS,
                    'source-layer': LAYER_IDS.INFO_RESULTS,
                    type: 'line',
                    paint: LINE_HIGHLIGHT_PAINT,
                },
            ],
        },
    },
    {
        id: LAYER_IDS.PANORAMA,
        type: LAYER_TYPE.VECTOR,
        layerOptions: { visible: true, zIndex: SYSTEM_LAYER_Z_INDEX, opacity: 1 },
        sourceOptions: {
            format: 'empty',
        },
        features: [],
        highlightedFeatureIds: [],
        mapboxStyle: {
            version: 8,
            sources: {
                [LAYER_IDS.PANORAMA]: {
                    type: MAPBOX_LAYER_TYPE[LAYER_TYPE.VECTOR],
                },
            },
            sprite: '/-/assets/sprites/panorama',
            layers: [
                {
                    id: LAYER_IDS.PANORAMA,
                    source: LAYER_IDS.PANORAMA,
                    'source-layer': LAYER_IDS.PANORAMA,
                    type: 'symbol',
                    layout: {
                        'icon-image': 'panorama',
                        'icon-size': 1,
                        'icon-rotate': {
                            type: 'identity',
                            property: 'viewAngle',
                        },
                        visibility: 'visible',
                    },
                },
            ],
        },
    },
];
const deriveLayersFromUserLocationConfig = () => [
    {
        id: LAYER_IDS.USER_LOCATION,
        type: LAYER_TYPE.VECTOR,
        layerOptions: { visible: true, zIndex: SYSTEM_LAYER_Z_INDEX, opacity: 1 },
        sourceOptions: {
            format: 'empty',
        },
        features: [],
        highlightedFeatureIds: [],
        mapboxStyle: {
            version: 8,
            sources: {
                [LAYER_IDS.USER_LOCATION]: {
                    type: MAPBOX_LAYER_TYPE[LAYER_TYPE.VECTOR],
                },
            },
            layers: [
                {
                    id: LAYER_IDS.USER_LOCATION,
                    source: LAYER_IDS.USER_LOCATION,
                    'source-layer': LAYER_IDS.USER_LOCATION,
                    type: 'circle',
                    paint: CIRCLE_PAINT,
                },
                {
                    id: LAYER_IDS.USER_LOCATION,
                    source: LAYER_IDS.USER_LOCATION,
                    'source-layer': LAYER_IDS.USER_LOCATION,
                    type: 'fill',
                    paint: {
                        'fill-color': `rgba(${COLOR.DEFAULT[0]}, ${COLOR.DEFAULT[1]}, ${COLOR.DEFAULT[2]}, 0.2)`,
                    },
                },
            ],
        },
    },
];
export const deriveMapStateFromConfig = (config) => {
    const mapState = deriveStateFromMapcoreConfig(config.map);
    if (!mapState) {
        return;
    }
    const highlight = deriveLayerFromHighlightConfig();
    const measure = deriveLayerFromMeasureConfig();
    const notes = deriveLayersFromNotesConfig();
    const uiManager = deriveLayersFromUIManagerConfig();
    const info = deriveLayersFromInfoConfig();
    const search = deriveLayerFromMapSearchConfig(config.search);
    const userLocation = deriveLayersFromUserLocationConfig();
    const layers = [
        ...(mapState?.layers ?? []),
        highlight,
        measure,
        ...notes,
        ...uiManager,
        ...info,
        ...search,
        ...userLocation,
    ].map((layer, idx) => ({
        ...layer,
        layerOptions: {
            ...layer.layerOptions,
            zIndex: (layer.layerOptions.zIndex ?? 0) + idx,
        },
    }));
    return { ...mapState, layers };
};
//# sourceMappingURL=deriveMapStateFromConfig.js.map